<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query'
import { computed, type PropType, type Ref, ref, watch } from 'vue'
import {
  getInternationalBankAccountsAction,
  getInternationalPaypalBankAccountsAction,
  getInternationalRevolutBankAccountsAction,
  getInternationalWiseBankAccountsAction,
  getIranianBankAccountsAction,
  getIranianSrcBankAccountsAction,
} from '@/api/functions/bank-account/get-bank-accounts-action'
import { updateTransactionStatusAction } from '@/api/functions/transactions/transaction-status.action'
import { useMutationApi, useQueryApi } from '@/composables/useApi'
import { useGeneralSettings } from '@/composables/useGeneralSettings'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import BuyerPaymentConfirmModal from './BuyerPaymentConfirmModal.vue'
import BuyerPaymentModal from './BuyerPaymentModal.vue'
import ConfirmTransactionModal from './ConfirmTransactionModal.vue'
import SellerPaymentConfirmModal from './SellerPaymentConfirmModal.vue'
import SellerPaymentModal from './SellerPaymentModal.vue'
import SellerSettlementModal from './SellerSettlementModal.vue'

interface BuyerPayment {
  amount: number
  provider: string
  transferType: string
  tracking_code: string
  statusUpdatedAt: string
}

const props = defineProps({
  transaction: {
    type: Object,
  },
  buyerPayment: {
    type: Array as PropType<BuyerPayment[] | undefined>,
  },
})

const emit = defineEmits(['reload'])
const toast = useToast()
const queryClient = useQueryClient()

const showConfirmTransactionModal = ref(false)
const showBuyerPaymentModal = ref(false)
const showBuyerPaymentConfirmModal = ref(false)
const showSellerPaymentModal = ref(false)
const showSellerPaymentConfirmModal = ref(false)
const showSellerSettlementModal = ref(false)
const selectedStatus = ref(props.transaction?.status || '')

const isTransferred = computed(() => props?.transaction?.status === 'TRANSFERRED')
const { transactionsStatusOptions } = useGeneralSettings()
const activeStatusesStage1 = [
  'INITIAL_OWNER_CONFIRMATION_PENDING',
  'BUYER_PAYMENT_PENDING',
  'BUYER_PAYMENT_VERIFICATION_PENDING',
  'SELLER_PAYMENT_PENDING',
  'SELLER_PAYMENT_VERIFICATION_PENDING',
  'SETTLE_WITH_SELLER_PENDING',
  'FINISHED',
]

const activeStatusesStage2 = [
  'BUYER_PAYMENT_PENDING',
  'BUYER_PAYMENT_VERIFICATION_PENDING',
  'SELLER_PAYMENT_PENDING',
  'SELLER_PAYMENT_VERIFICATION_PENDING',
  'SETTLE_WITH_SELLER_PENDING',
  'FINISHED',
]

const activeStatusesStage3 = [
  'BUYER_PAYMENT_VERIFICATION_PENDING',
  'SELLER_PAYMENT_PENDING',
  'SELLER_PAYMENT_VERIFICATION_PENDING',
  'SETTLE_WITH_SELLER_PENDING',
  'FINISHED',
]

const activeStatusesStage4 = [
  // 'BUYER_PAYMENT_VERIFICATION_PENDING',
  'SELLER_PAYMENT_PENDING',
  'SELLER_PAYMENT_VERIFICATION_PENDING',
  'SETTLE_WITH_SELLER_PENDING',
  'FINISHED',
]

const activeStatusesStage5 = [
  // 'SELLER_PAYMENT_PENDING',
  'SELLER_PAYMENT_VERIFICATION_PENDING',
  'SETTLE_WITH_SELLER_PENDING',
  'FINISHED',
]

const activeStatusesStage6 = [
  // 'SELLER_PAYMENT_VERIFICATION_PENDING',
  'SETTLE_WITH_SELLER_PENDING',
  'FINISHED',
]

const activeStatusesStage7 = [
  // 'SELLER_PAYMENT_VERIFICATION_PENDING',
  // 'SETTLE_WITH_SELLER_PENDING',
  'FINISHED',
]

const activeStatusesStage8 = ['CANCELLED']

const activeStatusesStage9 = ['BUYER_REJECTED']

const activeStatusesStage10 = ['SELLER_REJECTED']

const activeStatusesStage11 = ['TRANSFERRED']

const isActiveStage1 = (status?: string) => {
  return status ? activeStatusesStage1.includes(status) : false
}

const isActiveStage2 = (status?: string) => {
  return status ? activeStatusesStage2.includes(status) : false
}

const isActiveStage3 = (status?: string) => {
  return status ? activeStatusesStage3.includes(status) : false
}

const isActiveStage4 = (status?: string) => {
  return status ? activeStatusesStage4.includes(status) : false
}

const isActiveStage5 = (status?: string) => {
  return status ? activeStatusesStage5.includes(status) : false
}

const isActiveStage6 = (status?: string) => {
  return status ? activeStatusesStage6.includes(status) : false
}

const isActiveStage7 = (status?: string) => {
  return status ? activeStatusesStage7.includes(status) : false
}

const isActiveStage8 = (status?: string) => {
  return status ? activeStatusesStage8.includes(status) : false
}

const isActiveStage9 = (status?: string) => {
  return status ? activeStatusesStage9.includes(status) : false
}

const isActiveStage10 = (status?: string) => {
  return status ? activeStatusesStage10.includes(status) : false
}

const isActiveStage11 = (status?: string) => {
  return status ? activeStatusesStage11.includes(status) : false
}

const updateStatusMutation = useMutationApi({
  mutationFn: (newStatus: string) => updateTransactionStatusAction(props.transaction!.uuid, { status: newStatus }),

  onSuccess: () => {
    toast.success('وضعیت با موفقیت تغییر کرد')
    emit('reload')

    queryClient.invalidateQueries({ queryKey: ['transaction', props.transaction?.uuid] })
  },

  onError: (err: unknown) => {
    toast.error(getErrorMessage(err))
  },
})

//        Start Of Bank Accounts Actions
const buyerUuid = computed(() => props.transaction?.buyer?.uuid)
const sellerUuid = computed(() => props.transaction?.seller?.uuid)

const canLoadBuyerAccounts = computed(() => !!buyerUuid.value)
const canLoadSellerAccounts = computed(() => !!sellerUuid.value)

function useUserBankAccountsQueries(userUuid: Ref<string | undefined>, enabled: Ref<boolean>) {
  return {
    iranianSrc: useQueryApi({
      queryKey: ['bankAccounts', userUuid, 'iranian-src'],
      queryFn: () => getIranianSrcBankAccountsAction(userUuid.value!),
      enabled,
      staleTime: 1000 * 60 * 5,
    }),

    iranian: useQueryApi({
      queryKey: ['bankAccounts', userUuid, 'iranian'],
      queryFn: () => getIranianBankAccountsAction(userUuid.value!),
      enabled,
      staleTime: 1000 * 60 * 5,
    }),

    internationalRevolut: useQueryApi({
      queryKey: ['bankAccounts', userUuid, 'international-revolut'],
      queryFn: () => getInternationalRevolutBankAccountsAction(userUuid.value!),
      enabled,
      staleTime: 1000 * 60 * 5,
    }),

    internationalPaypal: useQueryApi({
      queryKey: ['bankAccounts', userUuid, 'international-paypal'],
      queryFn: () => getInternationalPaypalBankAccountsAction(userUuid.value!),
      enabled,
      staleTime: 1000 * 60 * 5,
    }),

    international: useQueryApi({
      queryKey: ['bankAccounts', userUuid, 'international'],
      queryFn: () => getInternationalBankAccountsAction(userUuid.value!),
      enabled,
      staleTime: 1000 * 60 * 5,
    }),
    wise: useQueryApi({
      queryKey: ['bankAccounts', userUuid, 'wise'],
      queryFn: () => getInternationalWiseBankAccountsAction(userUuid.value!),
      enabled,
      staleTime: 1000 * 60 * 5,
    }),
  }
}

const buyerBankQueries = useUserBankAccountsQueries(buyerUuid, canLoadBuyerAccounts)
const sellerBankQueries = useUserBankAccountsQueries(sellerUuid, canLoadSellerAccounts)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toSelectOptions(arr: any[]) {
  return (
    arr?.map((item) => ({
      label: item.title || item.bankName || item.ownerFullName || item.accountNumber || '—',
      value: item.id,
      verified: item.verified,
    })) ?? []
  )
}

const buyerIranianSrc = computed(() => toSelectOptions(buyerBankQueries.iranianSrc.data.value?.data ?? []))

const buyerIranian = computed(() => toSelectOptions(buyerBankQueries.iranian.data.value?.data ?? []))

const buyerRevolut = computed(() => toSelectOptions(buyerBankQueries.internationalRevolut.data.value?.data ?? []))

const buyerPaypal = computed(() => toSelectOptions(buyerBankQueries.internationalPaypal.data.value?.data ?? []))

const buyerInternational = computed(() => toSelectOptions(buyerBankQueries.international.data.value?.data ?? []))

const buyerWise = computed(() => toSelectOptions(buyerBankQueries.wise.data.value?.data ?? []))

const sellerIranianSrc = computed(() => toSelectOptions(sellerBankQueries.iranianSrc.data.value?.data ?? []))

const sellerIranian = computed(() => toSelectOptions(sellerBankQueries.iranian.data.value?.data ?? []))

const sellerRevolut = computed(() => toSelectOptions(sellerBankQueries.internationalRevolut.data.value?.data ?? []))

const sellerPaypal = computed(() => toSelectOptions(sellerBankQueries.internationalPaypal.data.value?.data ?? []))

const sellerWise = computed(() => toSelectOptions(sellerBankQueries.wise.data.value?.data ?? []))
//         End Of Bank Accounts Actions
const sellerInternational = computed(() => toSelectOptions(sellerBankQueries.international.data.value?.data ?? []))

watch(
  () => props.transaction?.status,
  (newStatus) => {
    selectedStatus.value = newStatus || ''
  },
  { immediate: true }
)

watch(selectedStatus, (newValue) => {
  if (!newValue) return

  if (!props.transaction) return

  if (newValue === props.transaction.status) {
    // toast.info('این وضعیت هم‌اکنون فعال است');
    return
  }

  if (updateStatusMutation.isPending.value) return

  updateStatusMutation.mutate(newValue)
})

const handleStatus = (status: string) => {
  updateStatusMutation.mutate(status)
}
</script>

<template>
  <div class="w-full h-full flex flex-col justify-between gap-8 p-5 lg:p-8 bg-white border border-[#d8d8d8] rounded-xl">
    <!-- ___________________ Status Modals _____________________  -->
    <!-- 1  -->
    <ConfirmTransactionModal v-model="showConfirmTransactionModal" :transaction="transaction"
      :buyer-iranian-src="buyerIranianSrc" :buyer-iranian="buyerIranian" :buyer-revolut="buyerRevolut"
      :buyer-paypal="buyerPaypal" :buyer-international="buyerInternational" :buyer-wise="buyerWise" :seller-iranian-src="sellerIranianSrc"
      :seller-iranian="sellerIranian" :seller-revolut="sellerRevolut" :seller-paypal="sellerPaypal" :seller-wise="sellerWise"
      :seller-international="sellerInternational" @confirm="console.log('Done!')"
      @cancel="showConfirmTransactionModal = false" />

    <!-- 2  -->
    <BuyerPaymentModal v-model="showBuyerPaymentModal" :uuid="transaction?.uuid" @cancel="showBuyerPaymentModal = false"
      @confirm="handleStatus('BUYER_PAYMENT_VERIFICATION_PENDING')" />

    <!-- 3  -->
    <BuyerPaymentConfirmModal v-model="showBuyerPaymentConfirmModal" :uuid="transaction?.uuid"
      :receipts="transaction?.receipts?.iranian" :buyerPayment="buyerPayment"
      @cancel="showBuyerPaymentConfirmModal = false" @confirm="handleStatus('SELLER_PAYMENT_PENDING')" />

    <!--  4 -->
    <SellerPaymentModal v-model="showSellerPaymentModal" :uuid="transaction?.uuid"
      @cancel="showSellerPaymentModal = false" @confirm="handleStatus('SELLER_PAYMENT_VERIFICATION_PENDING')" />

    <!--  5 -->
    <SellerPaymentConfirmModal v-model="showSellerPaymentConfirmModal" :uuid="transaction?.uuid"
      :images="transaction?.receipts?.international" @cancel="showSellerPaymentConfirmModal = false"
      @confirm="handleStatus('SETTLE_WITH_SELLER_PENDING')" />

    <!-- 6  -->
    <SellerSettlementModal v-model="showSellerSettlementModal" :transaction="transaction"
      @cancel="showSellerSettlementModal = false" @finish="handleStatus('FINISHED')" />

    <!-- ___________________  Statuses _____________________  -->
    <div class="flex flex-col">
      <!-- 1 - در انتظار تایید طرفین -->
      <div class="flex gap-3">
        <div class="flex flex-col items-center">
          <div class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-bold"
            :class="isActiveStage1(transaction?.status)
              ? 'border-[#2d5396] bg-[#2d5396] text-white'
              : 'border-[#cbd5e1] bg-white text-transparent'">
            ✓
          </div>
          <div class="w-0.5 flex-1 bg-[#e5e9f0]"></div>
        </div>
        <div class="flex flex-1 items-center justify-between pb-5">
          <span class="text-[15px]"
            :class="isActiveStage1(transaction?.status) ? 'text-[#1b325a] font-semibold' : 'text-[#9aa2b1] font-medium'">
            در انتظار تایید طرفین
          </span>
          <button @click="showConfirmTransactionModal = true"
            :disabled="isTransferred ? true : isActiveStage1(transaction?.status) ? false : true"
            class="w-fit ps-2 pe-0.5 cursor-pointer flex items-center duration-300 h-8 justify-center rounded-lg text-[13px]"
            :class="isActiveStage1(transaction?.status) ? 'hover:bg-[#eaeef5] text-[#2d5396]' : 'text-[#d8d8d8]'">
            مشاهده
            <img :src="isActiveStage1(transaction?.status) ? '/svg/chevron-down.svg' : '/svg/chevron-down-inactive.svg'"
              width="22" alt="chevron" class="rotate-90" />
          </button>
        </div>
      </div>

      <!-- 2 در انتظار پرداخت خریدار -->
      <div class="flex gap-3">
        <div class="flex flex-col items-center">
          <div class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-bold"
            :class="isActiveStage2(transaction?.status)
              ? 'border-[#2d5396] bg-[#2d5396] text-white'
              : 'border-[#cbd5e1] bg-white text-transparent'">
            ✓
          </div>
          <div class="w-0.5 flex-1 bg-[#e5e9f0]"></div>
        </div>
        <div class="flex flex-1 items-center justify-between pb-5">
          <span class="text-[15px]"
            :class="isActiveStage2(transaction?.status) ? 'text-[#1b325a] font-semibold' : 'text-[#9aa2b1] font-medium'">
            در انتظار پرداخت خریدار
          </span>
          <button @click="showBuyerPaymentModal = true"
            :disabled="isTransferred ? true : isActiveStage2(transaction?.status) ? false : true"
            class="w-fit ps-2 pe-0.5 cursor-pointer flex items-center duration-300 h-8 justify-center rounded-lg text-[13px]"
            :class="isActiveStage2(transaction?.status) ? 'hover:bg-[#eaeef5] text-[#2d5396]' : 'text-[#d8d8d8]'">
            مشاهده
            <img :src="isActiveStage2(transaction?.status) ? '/svg/chevron-down.svg' : '/svg/chevron-down-inactive.svg'"
              width="22" alt="chevron" class="rotate-90" />
          </button>
        </div>
      </div>

      <!-- 3 در انتظار تایید پرداخت خریدار-->
      <div class="flex gap-3">
        <div class="flex flex-col items-center">
          <div class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-bold"
            :class="isActiveStage3(transaction?.status)
              ? 'border-[#2d5396] bg-[#2d5396] text-white'
              : 'border-[#cbd5e1] bg-white text-transparent'">
            ✓
          </div>
          <div class="w-0.5 flex-1 bg-[#e5e9f0]"></div>
        </div>
        <div class="flex flex-1 items-center justify-between pb-5">
          <span class="text-[15px]"
            :class="isActiveStage3(transaction?.status) ? 'text-[#1b325a] font-semibold' : 'text-[#9aa2b1] font-medium'">
            در انتظار تایید پرداخت خریدار
          </span>
          <button @click="showBuyerPaymentConfirmModal = true"
            :disabled="isTransferred ? true : isActiveStage3(transaction?.status) ? false : true"
            class="w-fit ps-2 pe-0.5 cursor-pointer flex items-center duration-300 h-8 justify-center rounded-lg text-[13px]"
            :class="isActiveStage3(transaction?.status) ? 'hover:bg-[#eaeef5] text-[#2d5396]' : 'text-[#d8d8d8]'">
            مشاهده
            <img :src="isActiveStage3(transaction?.status) ? '/svg/chevron-down.svg' : '/svg/chevron-down-inactive.svg'"
              width="22" alt="chevron" class="rotate-90" />
          </button>
        </div>
      </div>

      <!-- 4 در انتظار پرداخت فروشنده  -->
      <div class="flex gap-3">
        <div class="flex flex-col items-center">
          <div class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-bold"
            :class="isActiveStage4(transaction?.status)
              ? 'border-[#2d5396] bg-[#2d5396] text-white'
              : 'border-[#cbd5e1] bg-white text-transparent'">
            ✓
          </div>
          <div class="w-0.5 flex-1 bg-[#e5e9f0]"></div>
        </div>
        <div class="flex flex-1 items-center justify-between pb-5">
          <span class="text-[15px]"
            :class="isActiveStage4(transaction?.status) ? 'text-[#1b325a] font-semibold' : 'text-[#9aa2b1] font-medium'">
            در انتظار پرداخت فروشنده
          </span>
          <button @click="showSellerPaymentModal = true"
            :disabled="isTransferred ? true : isActiveStage4(transaction?.status) ? false : true"
            class="w-fit ps-2 pe-0.5 cursor-pointer flex items-center duration-300 h-8 justify-center rounded-lg text-[13px]"
            :class="isActiveStage4(transaction?.status) ? 'hover:bg-[#eaeef5] text-[#2d5396]' : 'text-[#d8d8d8]'">
            مشاهده
            <img :src="isActiveStage4(transaction?.status) ? '/svg/chevron-down.svg' : '/svg/chevron-down-inactive.svg'"
              width="22" alt="chevron" class="rotate-90" />
          </button>
        </div>
      </div>

      <!-- 5  در انتظار تایید پرداخت فروشنده -->
      <div class="flex gap-3">
        <div class="flex flex-col items-center">
          <div class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-bold"
            :class="isActiveStage5(transaction?.status)
              ? 'border-[#2d5396] bg-[#2d5396] text-white'
              : 'border-[#cbd5e1] bg-white text-transparent'">
            ✓
          </div>
          <div class="w-0.5 flex-1 bg-[#e5e9f0]"></div>
        </div>
        <div class="flex flex-1 items-center justify-between pb-5">
          <span class="text-[15px]"
            :class="isActiveStage5(transaction?.status) ? 'text-[#1b325a] font-semibold' : 'text-[#9aa2b1] font-medium'">
            در انتظار تایید پرداخت فروشنده
          </span>
          <button @click="showSellerPaymentConfirmModal = true"
            :disabled="isTransferred ? true : isActiveStage5(transaction?.status) ? false : true"
            class="w-fit ps-2 pe-0.5 cursor-pointer flex items-center duration-300 h-8 justify-center rounded-lg text-[13px]"
            :class="isActiveStage5(transaction?.status) ? 'hover:bg-[#eaeef5] text-[#2d5396]' : 'text-[#d8d8d8]'">
            مشاهده
            <img :src="isActiveStage5(transaction?.status) ? '/svg/chevron-down.svg' : '/svg/chevron-down-inactive.svg'"
              width="22" alt="chevron" class="rotate-90" />
          </button>
        </div>
      </div>

      <!-- 6 تسویه با فروشنده توسط پی‌جیب   -->
      <div class="flex gap-3">
        <div class="flex flex-col items-center">
          <div class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-bold"
            :class="isActiveStage6(transaction?.status)
              ? 'border-[#2d5396] bg-[#2d5396] text-white'
              : 'border-[#cbd5e1] bg-white text-transparent'">
            ✓
          </div>
          <div class="w-0.5 flex-1 bg-[#e5e9f0]"></div>
        </div>
        <div class="flex flex-1 items-center justify-between pb-5">
          <span class="text-[15px]"
            :class="isActiveStage6(transaction?.status) ? 'text-[#1b325a] font-semibold' : 'text-[#9aa2b1] font-medium'">
            تسویه با فروشنده توسط پی‌جیب
          </span>
          <button @click="showSellerSettlementModal = true"
            :disabled="isTransferred ? true : isActiveStage6(transaction?.status) ? false : true"
            class="w-fit ps-2 pe-0.5 cursor-pointer flex items-center duration-300 h-8 justify-center rounded-lg text-[13px]"
            :class="isActiveStage6(transaction?.status) ? 'hover:bg-[#eaeef5] text-[#2d5396]' : 'text-[#d8d8d8]'">
            مشاهده
            <img :src="isActiveStage6(transaction?.status) ? '/svg/chevron-down.svg' : '/svg/chevron-down-inactive.svg'"
              width="22" alt="chevron" class="rotate-90" />
          </button>
        </div>
      </div>

      <!-- 7 پایان تراکنش -->
      <div class="flex items-center gap-3">
        <div class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-bold"
          :class="isActiveStage7(transaction?.status)
            ? 'border-[#2d5396] bg-[#2d5396] text-white'
            : 'border-[#cbd5e1] bg-white text-transparent'">
          ✓
        </div>
        <span class="text-[15px]"
          :class="isActiveStage7(transaction?.status) ? 'text-[#1b325a] font-semibold' : 'text-[#9aa2b1] font-medium'">
          پایان تراکنش
        </span>
      </div>

      <!-- 8  لغو شده -->
      <div v-if="isActiveStage8(transaction?.status)" class="flex items-center gap-3">
        <div
          class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-bold border-[#2d5396] bg-[#2d5396] text-white">
          ✓
        </div>
        <span class="text-[15px] font-semibold text-[#1b325a]">لغو شده</span>
      </div>

      <!-- 9 کنسل شده توسط خریدار-->
      <div v-if="isActiveStage9(transaction?.status)" class="flex items-center gap-3">
        <div
          class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-bold border-[#2d5396] bg-[#2d5396] text-white">
          ✓
        </div>
        <span class="text-[15px] font-semibold text-[#1b325a]">کنسل شده توسط خریدار</span>
      </div>

      <!-- 10 کنسل شده توسط فروشنده-->
      <div v-if="isActiveStage10(transaction?.status)" class="flex items-center gap-3">
        <div
          class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-bold border-[#2d5396] bg-[#2d5396] text-white">
          ✓
        </div>
        <span class="text-[15px] font-semibold text-[#1b325a]">کنسل شده توسط فروشنده</span>
      </div>

      <!-- 11 منتقل شده-->
      <div v-if="isActiveStage11(transaction?.status)" class="flex items-center gap-3">
        <div
          class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-bold border-[#2d5396] bg-[#2d5396] text-white">
          ✓
        </div>
        <span class="text-[15px] font-semibold text-[#1b325a]">منتقل شده</span>
      </div>
    </div>

    <!-- Divider  -->
    <div class="border-t h-0.5 w-full border-[#eaeef5]"></div>

    <!-- ___________________ Status Select ___________________ -->
    <div class="flex items-center gap-3 pt-4 text-[13px] text-[#4e648c] font-medium -mt-2">
      <span class="text-[#2d5396] text-lg font-medium">وضعیت</span>
      <Select variant="checkbox" v-model="selectedStatus" :disabled="isTransferred" class="h-12!"
        :options="transactionsStatusOptions" />
    </div>
  </div>
</template>
